/**
 * useUpiIntentActions – React hook for UPI Intent payment flow
 *
 * Drop-in alternative to usePaymentActions when the env flag
 * NEXT_PUBLIC_USE_UPI_INTENT is enabled.
 *
 * Flow:
 *  1. createUpiIntent(amount) → calls projectPB POST /upi/create-intent
 *     Returns UPI deep-link, QR data, app deep-links, transactionRef
 *  2. User pays via their UPI app (GPay / PhonePe / Paytm / …)
 *  3. verifyUpiPayment(transactionRef, utrNumber) → calls POST /upi/verify
 *     Submits UTR, backend verifies + credits wallet
 *  4. pollUpiStatus(transactionRef) → calls GET /upi/status
 */

import { useState, useCallback, useRef } from 'react';
import useApiCall from '../../services/api/useApiCall';
import { buildApiConfig } from '../../services/api/apiClient';
import { endPointEnums } from '../../services/api/endpoints';

export default function useUpiIntentActions({ userId, userPhone, onWalletUpdate }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [intentData, setIntentData] = useState(null); // { upiUrl, qrData, appLinks, transactionRef, ... }
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifySuccess, setVerifySuccess] = useState(false);
  const { makeApiCall } = useApiCall();
  const pollingRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // ─── 1. Create UPI Intent ──────────────────────────────────────────────────

  const createUpiIntent = useCallback(
    async (amount) => {
      if (!userId) {
        setError('User ID is required');
        return null;
      }
      if (!amount || Number(amount) <= 0) {
        setError('Please enter a valid amount');
        return null;
      }

      setLoading(true);
      setError('');
      setIntentData(null);
      setVerifySuccess(false);
      setStatus('');

      try {
        const apiConfig = buildApiConfig(endPointEnums.UPI_CREATE_INTENT, {
          amount: Number(amount),
          customerId: userId,
          phone: String(userPhone || ''),
          email: 'user@app.com',
        });

        const res = await makeApiCall(apiConfig);
        const data = res?.data || res;

        if (data?.upiUrl && data?.transactionRef) {
          setIntentData(data);
          setStatus('CREATED');
          return data;
        } else {
          throw new Error('Failed to create UPI intent. Missing data.');
        }
      } catch (err) {
        console.error('[UPI Intent] Create error:', err);
        setError(err?.message || 'Failed to create UPI intent');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [userId, userPhone, makeApiCall]
  );

  // ─── 2. Confirm UPI Payment (auto — no UTR needed) ──────────────────────────

  const confirmUpiPayment = useCallback(
    async (transactionRef) => {
      if (!transactionRef) {
        setError('Transaction reference is required');
        return false;
      }

      setVerifyLoading(true);
      setError('');
      setVerifySuccess(false);

      try {
        const apiConfig = buildApiConfig(endPointEnums.UPI_CONFIRM_PAYMENT, {
          transactionRef,
        });

        const res = await makeApiCall(apiConfig);
        const data = res?.data || res;

        if (data?.status === 'SUCCESS') {
          setStatus('SUCCESS');
          setVerifySuccess(true);
          stopPolling();

          // Refresh wallet balance
          const refreshWallet = async () => {
            if (onWalletUpdate) await onWalletUpdate();
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('walletUpdated'));
            }
          };

          // Staggered refresh to ensure backend has committed
          setTimeout(refreshWallet, 500);
          setTimeout(refreshWallet, 2000);
          setTimeout(refreshWallet, 5000);

          return true;
        } else {
          setStatus(data?.status || 'PENDING');
          setError(data?.message || 'Payment confirmation pending');
          return false;
        }
      } catch (err) {
        console.error('[UPI Intent] Confirm error:', err);
        setError(err?.message || 'Confirmation failed. Please try again.');
        return false;
      } finally {
        setVerifyLoading(false);
      }
    },
    [makeApiCall, onWalletUpdate, stopPolling]
  );

  // ─── 3. Verify UPI Payment (legacy — submit UTR) ──────────────────────────

  const verifyUpiPayment = useCallback(
    async (transactionRef, utrNumber) => {
      if (!transactionRef || !utrNumber) {
        setError('Transaction reference and UTR number are required');
        return false;
      }

      setVerifyLoading(true);
      setError('');
      setVerifySuccess(false);

      try {
        const apiConfig = buildApiConfig(endPointEnums.UPI_VERIFY, {
          transactionRef,
          utrNumber: String(utrNumber).trim(),
        });

        const res = await makeApiCall(apiConfig);
        const data = res?.data || res;

        if (data?.status === 'SUCCESS') {
          setStatus('SUCCESS');
          setVerifySuccess(true);

          // Refresh wallet balance
          const refreshWallet = async () => {
            if (onWalletUpdate) await onWalletUpdate();
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('walletUpdated'));
            }
          };

          // Staggered refresh to ensure backend has committed
          setTimeout(refreshWallet, 500);
          setTimeout(refreshWallet, 2000);
          setTimeout(refreshWallet, 5000);

          return true;
        } else {
          setStatus(data?.status || 'PENDING');
          setError(data?.message || 'Payment verification pending');
          return false;
        }
      } catch (err) {
        console.error('[UPI Intent] Verify error:', err);
        setError(err?.message || 'Verification failed. Please try again.');
        return false;
      } finally {
        setVerifyLoading(false);
      }
    },
    [makeApiCall, onWalletUpdate]
  );

  // ─── 3. Poll UPI Payment Status ───────────────────────────────────────────

  const pollUpiStatus = useCallback(
    async (transactionRef) => {
      if (!transactionRef) return null;

      try {
        const apiConfig = buildApiConfig(endPointEnums.UPI_STATUS, {
          transactionRef,
        });

        const res = await makeApiCall(apiConfig);
        const data = res?.data || res;
        setStatus(data?.status || '');
        return data;
      } catch (err) {
        console.error('[UPI Intent] Poll error:', err);
        return null;
      }
    },
    [makeApiCall]
  );

  // ─── 4. Start / Stop auto-polling ──────────────────────────────────────────

  const startPolling = useCallback(
    (transactionRef, intervalMs = 5000) => {
      stopPolling(); // clear any existing interval
      pollingRef.current = setInterval(async () => {
        const data = await pollUpiStatus(transactionRef);
        if (data?.status === 'SUCCESS') {
          stopPolling();
          setVerifySuccess(true);
          if (onWalletUpdate) await onWalletUpdate();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('walletUpdated'));
          }
        }
      }, intervalMs);
    },
    [pollUpiStatus, onWalletUpdate, stopPolling]
  );

  // ─── Reset ─────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    stopPolling();
    setLoading(false);
    setError('');
    setStatus('');
    setIntentData(null);
    setVerifyLoading(false);
    setVerifySuccess(false);
  }, [stopPolling]);

  return {
    // State
    loading,
    error,
    status,
    intentData,
    verifyLoading,
    verifySuccess,

    // Actions
    createUpiIntent,
    confirmUpiPayment,
    verifyUpiPayment,
    pollUpiStatus,
    startPolling,
    stopPolling,
    reset,
  };
}
